import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input } from "@material-tailwind/react";
import { FcGoogle } from "react-icons/fc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import MaterialInput from "../input/MaterialInput";
import Chat from "../../images/chat.jpg";
import { useAuth } from "../../contexts/AuthContext";
import {
  FieldError,
  FormFlashMessages,
  loginSchema,
} from "../../helpers/authHelpers";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Login({ callback = () => { }, inModal = true }) {
  const [forgetPassword, setforgetPassword] = useState(false);

  const {
    login: loginUser,
    loggingIn,
    error: authError,
    success: authSuccess,
  } = useAuth();

  // Close modal on successful login
  React.useEffect(() => {
    if (authSuccess && inModal) {
      setTimeout(() => {
        callback();
      }, 1500); // Give user time to see success message
    }
  }, [authSuccess, inModal, callback]);

  const {
    register: formLogin,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(loginSchema),
  });

  const handleLogin = (data) => {
    try {
      loginUser({ email: data.email, password: data.password });
    } catch (error) {
      // Handle error silently
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${API_BASE_URL}/auth/google/authorize`;
  };

  if (!forgetPassword) {
    return (
      <div className="w-full max-w-[400px] bg-white rounded overflow-hidden text-[15px] flex flex-col gap-2 justify-between text-gray-600">
        {inModal ? (
          <div>
            <img src={Chat} className="w-full h-auto max-h-[140px] object-cover" alt="" />
          </div>
        ) : (
          <></>
        )}

        <div className="p-4 sm:p-8 z-10">
          <div className=" flex flex-col gap-4">
            <div className="flex flex-col gap-4 ">
              {inModal ? (
                <>
                  <h1 className="font-bold text-center my-4">
                    Sign in to continue and save your conversation
                  </h1>
                </>
              ) : (
                <></>
              )}

              <FormFlashMessages
                errors={errors}
                authSuccess={authSuccess}
                authError={authError}
              />

              <form
                className="flex flex-col gap-4 "
                onSubmit={handleSubmit(handleLogin)}>
                <Input
                  label="Email"
                  name="email"
                  {...formLogin("email")}
                  error={!!errors.email}
                  disabled={loggingIn}
                />
                <FieldError errorField={errors.email} />

                <Input
                  label="Password"
                  name="password"
                  type="password"
                  {...formLogin("password")}
                  error={!!errors.password}
                  disabled={loggingIn}
                />
                <FieldError errorField={errors.password} />

                <div className="text-primary font-bold">
                  {!inModal ? (
                    <>
                      <Link to="/password/reset">
                        {" "}
                        <span
                          className="cursor-pointer"
                          onClick={() => setforgetPassword(() => true)}>
                          Forgot password?
                        </span>{" "}
                      </Link>
                    </>
                  ) : (
                    <>
                      <span
                        className="cursor-pointer"
                        onClick={() => setforgetPassword(() => true)}>
                        Forgot password?
                      </span>
                    </>
                  )}
                </div>
                <Button
                  className="bg-primary"
                  type="submit"
                  loading={loggingIn}
                  disabled={loggingIn || authSuccess}>
                  Sign In
                </Button>
              </form>
            </div>

            <h1 className="text-center font-thin">
              Create a new account?{" "}
              {!inModal ? (
                <>
                  <Link to="/signup">
                    <span className=" text-primary cursor-pointer">
                      Sign Up
                    </span>
                  </Link>
                </>
              ) : (
                <>
                  <span
                    className=" text-primary cursor-pointer"
                    onClick={callback}>
                    Sign Up
                  </span>
                </>
              )}{" "}
            </h1>

            <div className="border-t mt-2 border-gray-400 flex justify-center items-center">
              <div className="bg-white relative -top-[20px] p-2 text-gray-500">
                or
              </div>
            </div>

            <button
              className="-mt-4 w-full h-[40px] border border-gray-300 flex justify-between px-2 items-center rounded hover:bg-gray-50"
              onClick={handleGoogleLogin}
              type="button"
            >
              Continue with Google
              <span>
                <FcGoogle size={18} />
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="w-full max-w-[400px] bg-white rounded overflow-hidden text-[15px] flex flex-col gap-2 justify-between text-gray-600">
        {inModal ? (
          <>
            <div>
              <img src={Chat} className="w-full h-auto max-h-[140px] object-cover" alt="" />
            </div>
          </>
        ) : (
          <></>
        )}

        <div className="p-4 sm:p-8 ">
          <div className=" flex flex-col gap-4">
            <div className="flex flex-col gap-4 ">
              <h1 className="font-bold text-center my-4">
                Get a password reset link
              </h1>
              <div>
                {" "}
                <MaterialInput text="Email address" />
              </div>

              <button className="w-full h-[40px] font-bold text-white bg-orange-400 shadow flex justify-center items-center rounded hover:bg-orange-500 transition-colors">
                Send link
              </button>
            </div>

            <h1 className="text-center font-thin">
              Remember your password?{" "}
              <span
                className=" text-orange-400 cursor-pointer"
                onClick={() => setforgetPassword(false)}>
                Sign In
              </span>{" "}
            </h1>
          </div>
        </div>
      </div>
    );
  }
}
